import { useEffect, useState } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  getStudentById,
  updateStudent,
  changePassword,
} from "../../services/studentServices";
import { uploadImages, deleteImage } from "../../services/uploaderServices";
import { getCookie } from "../../utils";
import {
  CheckCircle,
  Clock,
  Edit,
  Save,
  X,
  Plus,
  Trash,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import Breadcrumb from "../../components/Breadcrumb";
import toast from "react-hot-toast";

const StudentRegistration = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStudent, setEditedStudent] = useState(null);
  const userId = getCookie("userId");

  // Upload states
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBonafide, setUploadingBonafide] = useState(false);
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newBonafideFile, setNewBonafideFile] = useState(null);

  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        if (!userId) throw new Error("User ID not found in cookies");
        const data = await getStudentById(userId);
        setStudent(data.data);
        setEditedStudent(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStudent();
  }, [userId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const handleEditChange = (field, value) => {
    setEditedStudent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInstitutionChange = (index, field, value) => {
    setEditedStudent((prev) => {
      const updatedInstitutions = [...prev.institutionDetails];
      updatedInstitutions[index] = {
        ...updatedInstitutions[index],
        [field]: value,
      };
      return {
        ...prev,
        institutionDetails: updatedInstitutions,
      };
    });
  };

  const handleAddInstitution = () => {
    setEditedStudent((prev) => ({
      ...prev,
      institutionDetails: [
        ...prev.institutionDetails,
        {
          institutionName: "",
          location: "",
          degree: "",
          fieldOfStudy: "",
          yearOfStart: "",
          targetedYearOfCompletion: "",
        },
      ],
    }));
  };

  const handleDeleteInstitution = (index) => {
    if (editedStudent.institutionDetails.length > 1) {
      setEditedStudent((prev) => ({
        ...prev,
        institutionDetails: prev.institutionDetails.filter(
          (_, i) => i !== index
        ),
      }));
    } else {
      toast.error("At least one institution is required");
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("images", file);
      const response = await uploadImages(formData);
      if (response.success && response.images?.[0]?.imageUrl) {
        setEditedStudent((prev) => ({
          ...prev,
          photo: response.images[0].imageUrl,
        }));
        setNewPhotoFile(file);
        toast.success("Photo uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload photo";
      toast.error(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!editedStudent?.photo) return;
    try {
      await deleteImage(editedStudent.photo);
      setEditedStudent((prev) => ({
        ...prev,
        photo: "",
      }));
      setNewPhotoFile(null);
      toast.success("Photo removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete photo";
      toast.error(errorMessage);
    }
  };

  const handleBonafideUpload = async (file) => {
    if (!file) return;
    setUploadingBonafide(true);
    try {
      const formData = new FormData();
      formData.append("images", file);
      const response = await uploadImages(formData);
      if (response.success && response.images?.[0]?.imageUrl) {
        setEditedStudent((prev) => ({
          ...prev,
          bonafideCertificate: response.images[0].imageUrl,
        }));
        setNewBonafideFile(file);
        toast.success("Bonafide certificate uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to upload bonafide certificate";
      toast.error(errorMessage);
    } finally {
      setUploadingBonafide(false);
    }
  };

  const handleDeleteBonafide = async () => {
    if (!editedStudent?.bonafideCertificate) return;
    try {
      await deleteImage(editedStudent.bonafideCertificate);
      setEditedStudent((prev) => ({
        ...prev,
        bonafideCertificate: "",
      }));
      setNewBonafideFile(null);
      toast.success("Bonafide certificate removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete bonafide certificate";
      toast.error(errorMessage);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      await updateStudent(userId, editedStudent);
      setStudent(editedStudent);
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (err) {
      toast.error(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedStudent(student);
    setNewPhotoFile(null);
    setNewBonafideFile(null);
    setIsEditing(false);
  };

  // Password change handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      toast.error("All password fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    try {
      setChangingPassword(true);
      await changePassword(userId, passwordData);
      toast.success("Password changed successfully!");

      // Reset form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast.error(err.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  if (loading && !student) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error</h4>
            <p>{error}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!student) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-muted">No student data found</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main>
        <Breadcrumb title="Student Dashboard" />
        <div className="it-teacher-details-area pt-50 pb-50">
          <div className="container">
            <div className="it-teacher-details-wrap">
              <Tabs
                selectedIndex={activeTab}
                onSelect={(index) => setActiveTab(index)}
              >
                <div className="row">
                  {/* Sidebar */}
                  <div className="col-xl-3 col-lg-3">
                    <div className="it-teacher-details-left">
                      <div className="it-teacher-details-left-thumb">
                        <img
                          className="rounded-5"
                          style={{
                            width: "100%",
                            height: "300px",
                            objectFit: "cover",
                          }}
                          src={student.photo}
                          alt={student.fullName}
                          onError={(e) => {
                            e.target.src = "default-avatar.png";
                          }}
                        />
                      </div>

                      <div className="it-teacher-details-left-social text-center pb-4">
                        <h5 style={{ color: "#704fe6" }}>{student.fullName}</h5>
                        <div className="d-flex gap-2 justify-content-center mt-3">
                          <span
                            className="badge"
                            style={{
                              backgroundColor:
                                student.status === "approved"
                                  ? "#28a745"
                                  : "#ffc107",
                              color: "white",
                              padding: "8px 16px",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {student?.status?.charAt(0).toUpperCase() +
                              student?.status?.slice(1)}
                          </span>
                          <span
                            className="badge"
                            style={{
                              backgroundColor:
                                student.loginAuthStatus === "active"
                                  ? "#28a745"
                                  : "#dc3545",
                              color: "white",
                              padding: "8px 16px",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {student.loginAuthStatus === "active"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>
                      </div>

                      <TabList
                        style={{ listStyle: "none", padding: 0 }}
                        className="member-tab mt-4"
                      >
                        <Tab>
                          <button className="ed-btn-theme w-100 mb-2">
                            Personal Info
                          </button>
                        </Tab>
                        <Tab>
                          <button className="ed-btn-theme w-100 mb-2">
                            Education
                          </button>
                        </Tab>
                        <Tab>
                          <button className="ed-btn-theme w-100 mb-2">
                            Documents
                          </button>
                        </Tab>
                        <Tab>
                          <button className="ed-btn-theme w-100 mb-2">
                            Edit Profile
                          </button>
                        </Tab>
                        <Tab>
                          <button className="ed-btn-theme w-100 mb-2">
                            Change Password
                          </button>
                        </Tab>
                      </TabList>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="col-xl-9 col-lg-9">
                    {/* Personal Information Tab */}
                    <TabPanel>
                      <div className="it-teacher-details-right">
                        <div className="it-teacher-details-right-content">
                          <h5 className="mb-20" style={{ color: "#0b72ba" }}>
                            Personal Information
                          </h5>
                          <div className="row">
                            <div className="col-xl-6 col-lg-6 col-md-6">
                              <div className="it-teacher-details-left-info">
                                <ul>
                                  <li>
                                    <i
                                      className="fa-regular fa-envelope"
                                      style={{ color: "#0e2a46" }}
                                    ></i>
                                    <a>{student.email}</a>
                                    {student.isEmailVerified && (
                                      <span
                                        className="ms-2"
                                        style={{
                                          color: "#28a745",
                                          fontSize: "12px",
                                        }}
                                      >
                                        <CheckCircle
                                          className="d-inline"
                                          size={14}
                                        />{" "}
                                        Verified
                                      </span>
                                    )}
                                  </li>
                                  <li>
                                    <i
                                      className="fa-regular fa-mobile"
                                      style={{ color: "#0e2a46" }}
                                    ></i>
                                    <a>{student.mobile}</a>
                                    {student.isMobileVerified && (
                                      <span
                                        className="ms-2"
                                        style={{
                                          color: "#28a745",
                                          fontSize: "12px",
                                        }}
                                      >
                                        <CheckCircle
                                          className="d-inline"
                                          size={14}
                                        />{" "}
                                        Verified
                                      </span>
                                    )}
                                  </li>
                                  <li>
                                    <i
                                      className="fa-regular fa-calendar"
                                      style={{ color: "#0e2a46" }}
                                    ></i>
                                    <a>{formatDate(student.dateOfBirth)}</a>
                                  </li>
                                  <li>
                                    <i
                                      className="fa-regular fa-map-marker-alt"
                                      style={{ color: "#0e2a46" }}
                                    ></i>
                                    <a>{student.address}</a>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>

                    {/* Education Details Tab */}
                    <TabPanel>
                      <div className="it-teacher-details-right">
                        <div className="it-teacher-details-right-content">
                          <h5 className="mb-20" style={{ color: "#0b72ba" }}>
                            Education Details
                          </h5>
                          {student.institutionDetails.map(
                            (institution, index) => (
                              <div
                                key={index}
                                className="border border-secondary rounded p-4 mb-4"
                                style={{ borderColor: "#dee2e6" }}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 style={{ color: "#0b72ba" }}>
                                    Institution {index + 1}
                                  </h6>
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor: "#704fe6",
                                      color: "white",
                                      fontSize: "11px",
                                    }}
                                  >
                                    {institution.yearOfStart} -{" "}
                                    {institution.targetedYearOfCompletion}
                                  </span>
                                </div>
                                <div className="row">
                                  <div className="col-xl-6 col-lg-6">
                                    <div className="it-teacher-details-left-info mb-0">
                                      <ul>
                                        <li>
                                          <i
                                            className="fa-regular fa-building"
                                            style={{ color: "#0e2a46" }}
                                          ></i>
                                          <a>{institution.institutionName}</a>
                                        </li>
                                        <li>
                                          <i
                                            className="fa-regular fa-map-marker-alt"
                                            style={{ color: "#0e2a46" }}
                                          ></i>
                                          <a>{institution.location}</a>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  <div className="col-xl-6 col-lg-6">
                                    <div className="it-teacher-details-left-info mb-0">
                                      <ul>
                                        <li>
                                          <i
                                            className="fa-regular fa-graduation-cap"
                                            style={{ color: "#0e2a46" }}
                                          ></i>
                                          <a>{institution.degree}</a>
                                        </li>
                                        <li>
                                          <i
                                            className="fa-regular fa-book"
                                            style={{ color: "#0e2a46" }}
                                          ></i>
                                          <a>{institution.fieldOfStudy}</a>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          )}

                          <div
                            className={`p-3 rounded ${
                              student.hasCompletedSixCourses
                                ? "bg-success bg-opacity-10 border border-success"
                                : "bg-warning bg-opacity-10 border border-warning"
                            }`}
                          >
                            <div className="d-flex align-items-center gap-3">
                              {student.hasCompletedSixCourses ? (
                                <CheckCircle
                                  size={24}
                                  style={{ color: "#28a745" }}
                                />
                              ) : (
                                <Clock size={24} style={{ color: "#ffc107" }} />
                              )}
                              <div>
                                <p className="mb-0 fw-semibold">
                                  Six Courses Requirement
                                </p>
                                <p className="mb-0 text-muted small">
                                  {student.hasCompletedSixCourses
                                    ? "Completed"
                                    : "In Progress"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>

                    {/* Documents Tab */}
                    <TabPanel>
                      <div className="it-teacher-details-right">
                        <div className="it-teacher-details-right-content">
                          <h5 className="mb-20" style={{ color: "#0b72ba" }}>
                            Documents
                          </h5>
                          <div className="row">
                            <div className="col-xl-6 col-lg-6">
                              <div
                                className="border rounded overflow-hidden mb-4"
                                style={{ borderColor: "#dee2e6" }}
                              >
                                <div
                                  className="p-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <h6 className="mb-0">Profile Photo</h6>
                                </div>
                                <div className="p-3">
                                  <img
                                    src={student.photo}
                                    alt="Profile"
                                    className="img-fluid rounded mb-3"
                                    style={{
                                      width: "100%",
                                      height: "300px",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <a
                                    href={student.photo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm"
                                    style={{
                                      backgroundColor: "#704fe6",
                                      color: "white",
                                    }}
                                  >
                                    <i className="fa-regular fa-external-link me-2"></i>
                                    View Full Size
                                  </a>
                                </div>
                              </div>
                            </div>

                            <div className="col-xl-6 col-lg-6">
                              <div
                                className="border rounded overflow-hidden mb-4"
                                style={{ borderColor: "#dee2e6" }}
                              >
                                <div
                                  className="p-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <h6 className="mb-0">Bonafide Certificate</h6>
                                </div>
                                <div className="p-3">
                                  <img
                                    src={student.bonafideCertificate}
                                    alt="Bonafide Certificate"
                                    className="img-fluid rounded mb-3"
                                    style={{
                                      width: "100%",
                                      height: "300px",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <a
                                    href={student.bonafideCertificate}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm"
                                    style={{
                                      backgroundColor: "#28a745",
                                      color: "white",
                                    }}
                                  >
                                    <i className="fa-regular fa-download me-2"></i>
                                    Download Certificate
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>

                    {/* Edit Profile Tab */}
                    <TabPanel>
                      <div className="it-teacher-details-right">
                        <div className="it-teacher-details-right-content">
                          <div className="d-flex justify-content-between align-items-center mb-20">
                            <h5 style={{ color: "#0b72ba", margin: 0 }}>
                              Edit Profile
                            </h5>
                            {!isEditing && (
                              <button
                                onClick={() => setIsEditing(true)}
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#704fe6",
                                  color: "white",
                                }}
                              >
                                <Edit size={16} className="me-2" />
                                Enable Editing
                              </button>
                            )}
                          </div>

                          {/* Personal Information Section */}
                          <div className="mb-4">
                            <h6
                              className="mb-3"
                              style={{ color: "#0e2a46", fontWeight: 600 }}
                            >
                              Personal Information
                            </h6>
                            <div className="row">
                              <div className="col-xl-6 mb-3">
                                <label
                                  className="form-label"
                                  style={{ fontWeight: 500 }}
                                >
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={editedStudent?.fullName}
                                  onChange={(e) =>
                                    handleEditChange("fullName", e.target.value)
                                  }
                                  disabled={!isEditing}
                                  style={{
                                    backgroundColor: isEditing
                                      ? "white"
                                      : "#f8f9fa",
                                  }}
                                />
                              </div>

                              <div className="col-xl-6 mb-3">
                                <label
                                  className="form-label"
                                  style={{ fontWeight: 500 }}
                                >
                                  Date of Birth
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={formatDateForInput(
                                    editedStudent?.dateOfBirth
                                  )}
                                  onChange={(e) =>
                                    handleEditChange(
                                      "dateOfBirth",
                                      e.target.value
                                    )
                                  }
                                  disabled={!isEditing}
                                  style={{
                                    backgroundColor: isEditing
                                      ? "white"
                                      : "#f8f9fa",
                                  }}
                                />
                              </div>

                              <div className="col-xl-6 mb-3">
                                <label
                                  className="form-label"
                                  style={{ fontWeight: 500 }}
                                >
                                  Email
                                </label>
                                <input
                                  type="email"
                                  className="form-control"
                                  value={editedStudent?.email}
                                  disabled={true}
                                  style={{ backgroundColor: "#f8f9fa" }}
                                />
                                <small className="text-muted">
                                  Email cannot be changed
                                </small>
                              </div>

                              <div className="col-xl-6 mb-3">
                                <label
                                  className="form-label"
                                  style={{ fontWeight: 500 }}
                                >
                                  Mobile
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={editedStudent?.mobile}
                                  disabled={true}
                                  style={{ backgroundColor: "#f8f9fa" }}
                                />
                                <small className="text-muted">
                                  Mobile cannot be changed
                                </small>
                              </div>

                              <div className="col-xl-12 mb-3">
                                <label
                                  className="form-label"
                                  style={{ fontWeight: 500 }}
                                >
                                  Address
                                </label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={editedStudent?.address}
                                  onChange={(e) =>
                                    handleEditChange("address", e.target.value)
                                  }
                                  disabled={!isEditing}
                                  style={{
                                    backgroundColor: isEditing
                                      ? "white"
                                      : "#f8f9fa",
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Photo and Bonafide Upload Section */}
                          {isEditing && (
                            <div className="mb-4">
                              <h6
                                className="mb-3"
                                style={{ color: "#0e2a46", fontWeight: 600 }}
                              >
                                Documents
                              </h6>
                              <div className="row">
                                {/* Photo Upload */}
                                <div className="col-xl-6 mb-3">
                                  <label
                                    className="form-label"
                                    style={{ fontWeight: 500 }}
                                  >
                                    Upload Photo
                                  </label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    id="photoUploadEdit"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handlePhotoUpload(file);
                                    }}
                                    disabled={!isEditing}
                                  />
                                  <label
                                    htmlFor="photoUploadEdit"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "10px",
                                      background: uploadingPhoto
                                        ? "#f0f0f0"
                                        : "#fff",
                                      color: "#0E2A46",
                                      borderRadius: "8px",
                                      border: "1.5px solid #bbb",
                                      padding: "13px 28px",
                                      width: "100%",
                                      textAlign: "center",
                                      cursor: uploadingPhoto
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: 500,
                                      fontSize: "0.9rem",
                                      transition:
                                        "border 0.17s, box-shadow 0.17s",
                                      boxShadow: "0 1px 3px #0001",
                                      pointerEvents: uploadingPhoto
                                        ? "none"
                                        : "auto",
                                    }}
                                  >
                                    {uploadingPhoto
                                      ? "Uploading..."
                                      : "Choose Photo"}
                                  </label>
                                  {editedStudent?.photo && (
                                    <div
                                      style={{
                                        marginTop: "10px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            color: "#333",
                                          }}
                                        >
                                          {newPhotoFile
                                            ? `Selected: ${newPhotoFile.name}`
                                            : "Current Photo"}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={handleDeletePhoto}
                                          style={{
                                            padding: "4px 8px",
                                            backgroundColor: "#dc3545",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "11px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                      <img
                                        src={editedStudent.photo}
                                        alt="Photo preview"
                                        style={{
                                          maxWidth: "150px",
                                          maxHeight: "150px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                          border: "1px solid #ddd",
                                          boxShadow:
                                            "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Bonafide Upload */}
                                <div className="col-xl-6 mb-3">
                                  <label
                                    className="form-label"
                                    style={{ fontWeight: 500 }}
                                  >
                                    Upload Bonafide Certificate
                                  </label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    id="bonafideUploadEdit"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleBonafideUpload(file);
                                    }}
                                    disabled={!isEditing}
                                  />
                                  <label
                                    htmlFor="bonafideUploadEdit"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "10px",
                                      background: uploadingBonafide
                                        ? "#f0f0f0"
                                        : "#fff",
                                      color: "#0E2A46",
                                      borderRadius: "8px",
                                      border: "1.5px solid #bbb",
                                      padding: "13px 28px",
                                      width: "100%",
                                      cursor: uploadingBonafide
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: 500,
                                      fontSize: "0.9rem",
                                      transition:
                                        "border 0.17s, box-shadow 0.17s",
                                      boxShadow: "0 1px 3px #0001",
                                      pointerEvents: uploadingBonafide
                                        ? "none"
                                        : "auto",
                                    }}
                                  >
                                    {uploadingBonafide
                                      ? "Uploading..."
                                      : "Choose Bonafide"}
                                  </label>
                                  {editedStudent?.bonafideCertificate && (
                                    <div
                                      style={{
                                        marginTop: "10px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            color: "#333",
                                          }}
                                        >
                                          {newBonafideFile
                                            ? `Selected: ${newBonafideFile.name}`
                                            : "Current Certificate"}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={handleDeleteBonafide}
                                          style={{
                                            padding: "4px 8px",
                                            backgroundColor: "#dc3545",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "11px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                      <img
                                        src={editedStudent.bonafideCertificate}
                                        alt="Bonafide preview"
                                        style={{
                                          maxWidth: "150px",
                                          maxHeight: "150px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                          border: "1px solid #ddd",
                                          boxShadow:
                                            "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Institution Details Section */}
                          <div className="mb-4">
                            <h6
                              className="mb-3"
                              style={{ color: "#0e2a46", fontWeight: 600 }}
                            >
                              Institution Details
                            </h6>
                            {editedStudent?.institutionDetails?.map(
                              (institution, index) => (
                                <div
                                  key={index}
                                  className="border rounded p-3 mb-3"
                                  style={{
                                    borderColor: "#dee2e6",
                                    position: "relative",
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6
                                      className="mb-0"
                                      style={{ color: "#704fe6" }}
                                    >
                                      Institution {index + 1}
                                    </h6>
                                    {isEditing && (
                                      <div className="d-flex gap-2">
                                        {editedStudent.institutionDetails
                                          .length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleDeleteInstitution(index)
                                            }
                                            style={{
                                              backgroundColor: "#f8d7da",
                                              border: "none",
                                              borderRadius: "8px",
                                              cursor: "pointer",
                                              padding: "6px 10px",
                                              color: "#dc3545",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <Trash size={16} />
                                          </button>
                                        )}
                                        {index ===
                                          editedStudent.institutionDetails
                                            .length -
                                            1 && (
                                          <button
                                            type="button"
                                            onClick={handleAddInstitution}
                                            style={{
                                              backgroundColor: "#d1fae5",
                                              border: "none",
                                              borderRadius: "8px",
                                              cursor: "pointer",
                                              padding: "6px 10px",
                                              color: "#10b981",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <Plus size={16} />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="row">
                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Institution Name
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={institution.institutionName}
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "institutionName",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Location
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={institution.location}
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "location",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Degree
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={institution.degree}
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "degree",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Field of Study
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={institution.fieldOfStudy}
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "fieldOfStudy",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Year of Start
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={institution.yearOfStart}
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "yearOfStart",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>

                                    <div className="col-xl-6 mb-3">
                                      <label
                                        className="form-label"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Targeted Year of Completion
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={
                                          institution.targetedYearOfCompletion
                                        }
                                        onChange={(e) =>
                                          handleInstitutionChange(
                                            index,
                                            "targetedYearOfCompletion",
                                            e.target.value
                                          )
                                        }
                                        disabled={!isEditing}
                                        style={{
                                          backgroundColor: isEditing
                                            ? "white"
                                            : "#f8f9fa",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>

                          {/* Action Buttons */}
                          {isEditing && (
                            <div className="d-flex gap-2">
                              <button
                                onClick={handleSaveChanges}
                                className="btn"
                                style={{
                                  backgroundColor: "#28a745",
                                  color: "white",
                                }}
                                disabled={loading}
                              >
                                <Save size={16} className="me-2" />
                                {loading ? "Saving..." : "Save Changes"}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="btn"
                                style={{
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                }}
                                disabled={loading}
                              >
                                <X size={16} className="me-2" />
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabPanel>

                    {/* Change Password Tab */}
                    <TabPanel>
                      <div className="it-teacher-details-right">
                        <div className="it-teacher-details-right-content">
                          <h5 className="mb-20" style={{ color: "#0b72ba" }}>
                            Change Password
                          </h5>

                          <div className="mb-4">
                            <div
                              className="p-3 rounded mb-4"
                              style={{
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffc107",
                              }}
                            >
                              <div className="d-flex align-items-start gap-2">
                                <Lock
                                  size={20}
                                  style={{ color: "#856404", marginTop: "2px" }}
                                />
                                <div>
                                  <p
                                    className="mb-1 fw-semibold"
                                    style={{ color: "#856404" }}
                                  >
                                    Security Guidelines
                                  </p>
                                  <ul
                                    className="mb-0 ps-3"
                                    style={{
                                      color: "#856404",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <li>
                                      Password must be at least 6 characters
                                      long
                                    </li>
                                    <li>
                                      Use a strong password with a mix of
                                      letters and numbers
                                    </li>
                                    <li>
                                      Don't share your password with anyone
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            <form onSubmit={handleChangePassword}>
                              <div className="row">
                                {/* Current Password */}
                                <div className="col-xl-12 mb-3">
                                  <label
                                    className="form-label"
                                    style={{ fontWeight: 500 }}
                                  >
                                    Current Password
                                  </label>
                                  <div style={{ position: "relative" }}>
                                    <input
                                      type={
                                        showCurrentPassword
                                          ? "text"
                                          : "password"
                                      }
                                      className="form-control"
                                      value={passwordData.currentPassword}
                                      onChange={(e) =>
                                        handlePasswordChange(
                                          "currentPassword",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter current password"
                                      required
                                      style={{ paddingRight: "40px" }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowCurrentPassword(
                                          !showCurrentPassword
                                        )
                                      }
                                      style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#6c757d",
                                      }}
                                    >
                                      {showCurrentPassword ? (
                                        <EyeOff size={18} />
                                      ) : (
                                        <Eye size={18} />
                                      )}
                                    </button>
                                  </div>
                                </div>

                                {/* New Password */}
                                <div className="col-xl-6 mb-3">
                                  <label
                                    className="form-label"
                                    style={{ fontWeight: 500 }}
                                  >
                                    New Password
                                  </label>
                                  <div style={{ position: "relative" }}>
                                    <input
                                      type={
                                        showNewPassword ? "text" : "password"
                                      }
                                      className="form-control"
                                      value={passwordData.newPassword}
                                      onChange={(e) =>
                                        handlePasswordChange(
                                          "newPassword",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter new password"
                                      required
                                      minLength={6}
                                      style={{ paddingRight: "40px" }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowNewPassword(!showNewPassword)
                                      }
                                      style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#6c757d",
                                      }}
                                    >
                                      {showNewPassword ? (
                                        <EyeOff size={18} />
                                      ) : (
                                        <Eye size={18} />
                                      )}
                                    </button>
                                  </div>
                                </div>

                                {/* Confirm Password */}
                                <div className="col-xl-6 mb-3">
                                  <label
                                    className="form-label"
                                    style={{ fontWeight: 500 }}
                                  >
                                    Confirm New Password
                                  </label>
                                  <div style={{ position: "relative" }}>
                                    <input
                                      type={
                                        showConfirmPassword
                                          ? "text"
                                          : "password"
                                      }
                                      className="form-control"
                                      value={passwordData.confirmPassword}
                                      onChange={(e) =>
                                        handlePasswordChange(
                                          "confirmPassword",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Confirm new password"
                                      required
                                      minLength={6}
                                      style={{ paddingRight: "40px" }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowConfirmPassword(
                                          !showConfirmPassword
                                        )
                                      }
                                      style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#6c757d",
                                      }}
                                    >
                                      {showConfirmPassword ? (
                                        <EyeOff size={18} />
                                      ) : (
                                        <Eye size={18} />
                                      )}
                                    </button>
                                  </div>
                                </div>

                                {/* Password Match Indicator */}
                                {passwordData.newPassword &&
                                  passwordData.confirmPassword && (
                                    <div className="col-xl-12 mb-3">
                                      <div
                                        className={`p-2 rounded ${
                                          passwordData.newPassword ===
                                          passwordData.confirmPassword
                                            ? "bg-success bg-opacity-10 border border-success"
                                            : "bg-danger bg-opacity-10 border border-danger"
                                        }`}
                                        style={{ fontSize: "14px" }}
                                      >
                                        {passwordData.newPassword ===
                                        passwordData.confirmPassword ? (
                                          <span style={{ color: "#28a745" }}>
                                            <CheckCircle
                                              size={14}
                                              className="me-1"
                                            />
                                            Passwords match
                                          </span>
                                        ) : (
                                          <span style={{ color: "#dc3545" }}>
                                            <X size={14} className="me-1" />
                                            Passwords do not match
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>

                              {/* Action Buttons */}
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  type="submit"
                                  className="btn"
                                  style={{
                                    backgroundColor: "#704fe6",
                                    color: "white",
                                  }}
                                  disabled={changingPassword}
                                >
                                  <Lock size={16} className="me-2" />
                                  {changingPassword
                                    ? "Changing Password..."
                                    : "Change Password"}
                                </button>
                                <button
                                  type="button"
                                  onClick={resetPasswordForm}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#6c757d",
                                    color: "white",
                                  }}
                                  disabled={changingPassword}
                                >
                                  <X size={16} className="me-2" />
                                  Reset Form
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                  </div>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default StudentRegistration;
